import React, { useEffect, useRef, useState } from 'react';
import RoleCardIllustration, { RoleIllustrationType } from './RoleCardIllustration';
import './LandingRoleCard.css';

interface Feature {
  text: string;
}

export type RoleCardLayout = 'visual-right' | 'visual-left';

interface Props {
  role: RoleIllustrationType;
  title: string;
  tagline: string;
  accentClass: string;
  features: Feature[];
  layout: RoleCardLayout;
  index: number;
}

const LandingRoleCard: React.FC<Props> = ({
  role,
  title,
  tagline,
  accentClass,
  features,
  layout,
  index,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const layoutClass =
    layout === 'visual-right'
      ? 'landing-role-card--visual-right'
      : 'landing-role-card--visual-left';

  return (
    <article
      ref={ref}
      className={`landing-role-card ${accentClass} ${layoutClass} ${
        visible ? 'landing-role-card--visible' : ''
      }`}
      style={{ '--card-index': index } as React.CSSProperties}
    >
      <div className="landing-role-card__glow" aria-hidden />

      <div className="landing-role-card__inner">
        <div className="landing-role-card__content">
          <span className="landing-role-card__badge">{tagline}</span>
          <h3 className="landing-role-card__title">{title}</h3>
          <ul className="landing-role-card__list">
            {features.map((f) => (
              <li key={f.text}>
                <span className="landing-role-card__check" aria-hidden>
                  <svg viewBox="0 0 12 12" width="12" height="12">
                    <path
                      d="M2 6l3 3 5-6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                {f.text}
              </li>
            ))}
          </ul>
        </div>

        <div
          className={`landing-role-card__visual role-illus__visual-panel ${
            visible ? 'landing-role-card__visual--in' : ''
          }`}
        >
          <div className="landing-role-card__visual-frame">
            <RoleCardIllustration role={role} active={visible} />
          </div>
          <div className="landing-role-card__visual-noise" aria-hidden />
        </div>
      </div>
    </article>
  );
};

export default LandingRoleCard;
